/**
 * ShiftSection — Зөвшөөрлийн §5 «ӨДӨР ТУТМЫН АЖЛЫН ЭХЛЭЛ, ТӨГСГӨЛ».
 *
 * HRT-ийн `_step5` (AT_Step5)-ийн орлуулга.
 *
 * Наряд нь ажил дуустал НЭГ УДАА олгогддог ч, ажил ЭЭЛЖ БҮРД дахин
 * нээгдэж хаагддаг:
 *   • нээх  — ажилд оруулагч ажлын байрыг шалгаж зөвшөөрөл өгнө
 *   • хаах  — бригадын гишүүдийг ажлын байрнаас гаргана
 * Хоёр талд нь ажил гүйцэтгэгч мөн гарын үсэг зурна — нийт 4.
 *
 * ⚠ Гарын үсгийг ЗӨВХӨН ӨӨРӨӨ зурна (backend ч мөн адил шалгана). Наряд бол
 *   хуулийн баримт — өөр хүний өмнөөс «нээсэн/хаасан» гэж тэмдэглэх
 *   боломжгүй байх ёстой.
 *
 * Backend: /xab/hr-job-shifts (GET · POST · PUT · DELETE)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Input, Popconfirm, Tag, Tooltip, message } from "antd";
import moment from "moment";
import {
  getJobShifts,
  addJobShift,
  updateJobShift,
  deleteJobShift,
} from "src/api/high_risk";
import { useUserContext } from "src/contexts/userContext";

/** Нээх/хаах хос — маягтын §5-ийн хоёр багана. */
const SIDES = [
  {
    key: "open",
    title: "Ажилд оруулах зөвшөөрөл өгсөн",
    hint: "ажлын байр, нөхцөлийг шалгаж нээсэн",
    color: "#16A34A",
  },
  {
    key: "close",
    title: "Ажил дуусах",
    hint: "бригадын гишүүдийг ажлын байрнаас гаргаж хаасан",
    color: "#DC2626",
  },
];
const ROLES = [
  { key: "admitter", label: "Ажилд оруулагч" },
  { key: "performer", label: "Ажил гүйцэтгэгч" },
];

export default function ShiftSection({ jobId, readonly }) {
  const { user } = useUserContext();
  const myTn = String(user?.info?.tn || user?.tn || "");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      setData(await getJobShifts(jobId));
    } catch (ex) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items || [];

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      // ⚠ §4-т тухайн дүр ТОМИЛОГДООГҮЙ үед сервер зурахыг зөвшөөрөх ч
      //   анхааруулга буцаана. Үүнийг ЧИМЭЭГҮЙ өнгөрөөвөл ээлжийн гарын
      //   үсэг хариуцлагатай ИТА-тай холбогдоогүйг хэн ч мэдэхгүй.
      //   `duration` уртсгав — энгийн «OK»-оос ялгарах ёстой.
      if (res?.warning) message.warning(res.warning, 6);
      else if (ok) message.success(ok);
      load();
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Үйлдэл амжилтгүй.");
    } finally {
      setBusy(false);
    }
  };

  const sign = (row, sideKey, roleKey, signed) =>
    run(() =>
      updateJobShift({ id: row.id, sign: `${sideKey}_${roleKey}`, signed }),
    );

  if (!jobId) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
        <div className="font-semibold text-slate-700 mb-1">
          <i className="fas fa-calendar-alt mr-1" />
          Өдөр тутмын эхлэл, төгсгөл (§5)
        </div>
        <div style={{ fontSize: 12, color: "#8A94A6" }}>
          Нарядаа эхлээд хадгална уу.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
      <div className="font-semibold text-slate-700 mb-2 flex items-center justify-between">
        <span>
          <i className="fas fa-calendar-alt mr-1" />
          Өдөр тутмын ажлын эхлэл, төгсгөл (§5)
          <span style={{ fontWeight: 400, fontSize: 12, color: "#8A94A6" }}>
            {" "}
            · {items.length} ээлж · хаагдсан {data?.closed_count || 0}
          </span>
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {loading ? (
            <span style={{ fontSize: 12, color: "#8A94A6" }}>ачааллаж…</span>
          ) : null}
          {!readonly ? (
            <Button
              size="small"
              type="link"
              loading={busy}
              onClick={() =>
                run(
                  () =>
                    addJobShift({
                      job_id: jobId,
                      work_date: moment().format("YYYY-MM-DD"),
                    }),
                  "Ээлж нэмэгдлээ.",
                )
              }
            >
              <i className="fas fa-plus" style={{ marginRight: 4 }} />
              Ээлж нээх
            </Button>
          ) : null}
        </span>
      </div>

      {data?.unavailable ? (
        <div
          style={{
            fontSize: 12,
            color: "#92400E",
            background: "#FEF3C7",
            padding: "6px 8px",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          Хүснэгт бэлэн болоогүй байна — <code>create_hr_job_shift.sql</code>{" "}
          миграцыг ажиллуулна уу.
        </div>
      ) : null}

      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "#B0B7C3" }}>
          Ээлж бүртгээгүй. Ажил эхлэх бүрд «Ээлж нээх» дарж, ажилд оруулагч
          ба ажил гүйцэтгэгч хоёул гарын үсгээ зурна.
        </div>
      ) : (
        items.map((row) => (
          <div
            key={row.id}
            style={{
              border: "1px solid #E3E7EC",
              borderRadius: 8,
              padding: 8,
              marginBottom: 8,
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
                flexWrap: "wrap",
              }}
            >
              <Tag color="blue">{row.shift_no}-р ээлж</Tag>
              <DatePicker
                size="small"
                allowClear={false}
                disabled={readonly}
                format="YYYY-MM-DD"
                value={row.work_date ? moment(row.work_date) : null}
                onChange={(d) =>
                  run(() =>
                    updateJobShift({
                      id: row.id,
                      work_date: d ? d.format("YYYY-MM-DD") : null,
                    }),
                  )
                }
              />
              <Input
                size="small"
                style={{ width: 130 }}
                placeholder="Ротаци / ээлж"
                disabled={readonly}
                defaultValue={row.rotation || ""}
                onBlur={(e) =>
                  e.target.value !== (row.rotation || "") &&
                  run(() =>
                    updateJobShift({ id: row.id, rotation: e.target.value }),
                  )
                }
              />
              {row.is_closed ? (
                <Tag color="green">Хаагдсан</Tag>
              ) : row.is_open ? (
                <Tag color="orange">Нээлттэй</Tag>
              ) : null}
              <span style={{ flex: 1 }} />
              {!readonly && !row.is_open ? (
                <Popconfirm
                  title="Энэ ээлжийг устгах уу?"
                  okText="Устгах"
                  cancelText="Болих"
                  onConfirm={() =>
                    run(() => deleteJobShift(row.id), "Устгалаа.")
                  }
                >
                  <Button size="small" type="text" danger>
                    <i className="fas fa-times" />
                  </Button>
                </Popconfirm>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SIDES.map((side) => (
                <div
                  key={side.key}
                  style={{
                    flex: "1 1 260px",
                    minWidth: 0,
                    borderLeft: `3px solid ${side.color}`,
                    paddingLeft: 8,
                  }}
                >
                  <div style={{ fontSize: 11.5, fontWeight: 700 }}>
                    {side.title}
                  </div>
                  <div style={{ fontSize: 10, color: "#8A94A6", marginBottom: 4 }}>
                    {side.hint}
                  </div>
                  {ROLES.map((role) => {
                    const k = `${side.key}_${role.key}`;
                    const s = row.signatures?.[k] || {};
                    const mine = String(s.tn || "") === myTn;
                    return (
                      <div
                        key={k}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "2px 0",
                          fontSize: 11.5,
                        }}
                      >
                        <span style={{ width: 96, color: "#5B6472" }}>
                          {role.label}
                        </span>
                        {s.tn ? (
                          <Tooltip title={s.at || ""}>
                            <Tag
                              color="green"
                              style={{ fontSize: 10, cursor: mine ? "pointer" : "default" }}
                              onClick={() =>
                                !readonly && mine && sign(row, side.key, role.key, false)
                              }
                            >
                              <i className="fas fa-check mr-1" />
                              {s.name || s.tn}
                            </Tag>
                          </Tooltip>
                        ) : !readonly ? (
                          <Button
                            size="small"
                            type="primary"
                            ghost
                            style={{ fontSize: 11, height: 22, padding: "0 8px" }}
                            onClick={() => sign(row, side.key, role.key, true)}
                          >
                            Гарын үсэг
                          </Button>
                        ) : (
                          <span style={{ color: "#C6CBD4", fontSize: 11 }}>
                            зураагүй
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {items.length ? (
        <div style={{ fontSize: 11, color: "#8A94A6" }}>
          Зөвхөн <b>өөрийн</b> гарын үсгийг зурж, цуцлана. Хаахын өмнө ээлж
          нээгдсэн байх ёстой.
        </div>
      ) : null}
    </div>
  );
}
